import { consoleRoutes, type IconName, type VisitStatus } from "@sagot/constants";
import type { ChipTone } from "@sagot/ui";

/** The steps of one visit on the phone (mockup frames M3 to M8); the visit's brief (M2) is the visit address without a step. */
export const FIELD_STEPS = ["onsite", "checklist", "measures", "photos", "sign", "done"] as const;
export type FieldStep = (typeof FIELD_STEPS)[number];

export function isFieldStep(value: string | undefined): value is FieldStep {
  return FIELD_STEPS.some((step) => step === value);
}

/** Where a screen lives: `/w/:ws/my-day`, `/w/:ws/my-day/:visit`, `/w/:ws/my-day/:visit/:step`. */
export function fieldHref(ws: string, visitId?: string, step?: FieldStep): string {
  return consoleRoutes.myDay(ws, visitId, step);
}

/** The report is saved to the server this often while it has unsaved changes (MOB-003). */
export const AUTOSAVE_INTERVAL_MS = 10_000;

/** Upload queue and outbox: waits 1, 2, 4, 8 ... seconds between attempts, never longer than a minute. */
export const QUEUE_BACKOFF_BASE_MS = 1_000;
export const QUEUE_BACKOFF_CAP_MS = 60_000;
export const QUEUE_MAX_ATTEMPTS = 8;

/** Photos are shrunk on the phone before they go up: the longest edge and the JPEG quality. */
export const PHOTO_MAX_EDGE_PX = 1_600;
export const PHOTO_JPEG_QUALITY = 0.82;
export const PHOTO_JPEG_TYPE = "image/jpeg";
export const PHOTOS_PER_VISIT_MAX = 30;
export const PHOTO_URL_STALE_MS = 4 * 60_000;

/** Client-side stores: one IndexedDB database for photo blobs, localStorage for the small JSON drafts. */
export const FIELD_DB_NAME = "sagot-field";
export const FIELD_DB_VERSION = 1;
export const FIELD_DB_STORE = "photo-queue";
export const FIELD_STORAGE_PREFIX = "sagot.field";
export const draftStorageKey = (ws: string, visitId: string): string => `${FIELD_STORAGE_PREFIX}.draft.${ws}.${visitId}`;
export const outboxStorageKey = (ws: string): string => `${FIELD_STORAGE_PREFIX}.outbox.${ws}`;
export const GEO_CONSENT_KEY = `${FIELD_STORAGE_PREFIX}.share-location`;

/** Browser geolocation asked for at the tap only: coarse, fast, a few seconds at most (stored to three decimals by the server). */
export const GEO_TIMEOUT_MS = 6_000;
export const GEO_MAX_AGE_MS = 60_000;

export const REPORT_TEXT_MAX = 5_000;
export const REPORT_FOLLOW_UP_NOTE_MAX = 1_000;
export const REPORT_MEASUREMENTS_MAX = 50;
export const MEASUREMENT_NAME_MAX = 80;
export const MEASUREMENT_VALUE_MAX = 40;
export const MEASUREMENT_UNIT_MAX = 20;
export const CHECKLIST_NOTE_MAX = 500;
export const SIGNOFF_NAME_MAX = 160;
export const SIGNOFF_TITLE_MAX = 120;
export const REASON_MIN = 3;
export const REASON_MAX = 500;
export const PART_QUANTITY_MAX = 999;
export const PART_DESCRIPTION_MAX = 200;
export const PART_SERIAL_MAX = 80;
export const TIME_MINUTES_MAX = 1_440;
export const TIME_NOTE_MAX = 300;
export const CATALOGUE_SEARCH_LIMIT = 8;
export const CATALOGUE_SEARCH_DEBOUNCE_MS = 250;

export const SIGNATURE_WIDTH_PX = 640;
export const SIGNATURE_HEIGHT_PX = 240;
export const SIGNATURE_LINE_PX = 3;

/** The look of a visit's status chip on the phone (the same tones as the dispatch board). */
export const FIELD_STATUS_LOOK: Readonly<Record<VisitStatus, { tone: ChipTone; icon: IconName }>> = {
  proposed: { tone: "accent", icon: "calendar-clock" },
  scheduled: { tone: "brand", icon: "calendar" },
  dispatched: { tone: "info", icon: "truck" },
  on_site: { tone: "warning", icon: "map-pin" },
  completed: { tone: "success", icon: "check-circle" },
  cannot_complete: { tone: "danger", icon: "alert-triangle" },
  cancelled: { tone: "muted", icon: "x-circle" },
  no_access: { tone: "danger", icon: "lock" },
};

/** Statuses in which the engineer can still work the visit; anything else is read-only. */
export const FIELD_OPEN_STATUSES: readonly VisitStatus[] = ["scheduled", "dispatched", "on_site"];

export const FIELD_NAV_ICON: IconName = "calendar";

/** The step bar at the foot of the phone (mockup `fm-nav`). */
export const FIELD_NAV_ITEMS: readonly { step: FieldStep | "brief"; icon: IconName; labelKey: string }[] = [
  { step: "brief", icon: "truck", labelKey: "service.field.nav.visit" },
  { step: "checklist", icon: "clipboard-check", labelKey: "service.field.nav.checklist" },
  { step: "measures", icon: "package", labelKey: "service.field.nav.parts" },
  { step: "photos", icon: "camera", labelKey: "service.field.nav.photos" },
];

export const FIELD_ERROR_REASONS = ["report_incomplete", "report_locked", "visit_ended", "visit_transition", "visit_not_yours", "already_checked_in", "already_checked_out", "not_on_site", "reason_needed", "photo_not_image", "beyond_contract"] as const;

/** "Navigate" opens the engineer's maps app on this (the destination is appended, URL-encoded). */
export const MAPS_DIRECTIONS_URL = "https://www.google.com/maps/dir/?api=1&destination=";
